import { calcDocument, D, dec } from '@makila/fiscal';
/**
 * Fidelização (decisão de 21/10/2026): 1 ponto por cada 100 MT de compra;
 * 1 ponto vale 1 MT de desconto, até 50% da compra; só com cliente
 * identificado; cada ponto expira 12 meses depois de ganho (os mais antigos
 * gastam-se primeiro).
 *
 * Os pontos usados são DESCONTO (baixam a base do IVA), aplicado às linhas
 * como uma percentagem — sem gerente (é o programa da loja) e sem acumular:
 * não entra em linhas com promoção, preço regulado ou desconto manual.
 */
export const POINTS_EVERY_MT = 100;
export const MAX_REDEEM_PCT = 50;
export const POINTS_EXPIRY_MONTHS = 12;
/** Pontos ganhos numa venda (total pago, já com descontos). */
export const pointsEarned = (grossTotal) => Math.max(0, dec(grossTotal).div(POINTS_EVERY_MT).floor().toNumber());
/** Data de expiração dos pontos ganhos num dia (12 meses). */
export function pointsExpiry(at) {
    const d = new Date(`${at}T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + POINTS_EXPIRY_MONTHS);
    return d.toISOString().slice(0, 10);
}
/**
 * Saldo num dia: os pontos ganhos formam lotes (FIFO); usar ou anular tira dos
 * mais antigos ainda válidos; um lote que expira perde o que lhe restar.
 */
export function loyaltyBalance(entries, today) {
    const lots = [];
    const sorted = [...entries].sort((a, b) => a.at.localeCompare(b.at) || (a.kind === 'earn' ? -1 : 1));
    for (const e of sorted) {
        if (e.kind === 'earn') {
            lots.push({ left: e.points, expires: pointsExpiry(e.at) });
            continue;
        }
        let need = e.points;
        for (const l of lots) {
            if (need <= 0)
                break;
            if (l.expires <= e.at || l.left <= 0)
                continue;
            const q = Math.min(l.left, need);
            l.left -= q;
            need -= q;
        }
    }
    return lots.filter((l) => l.expires > today).reduce((s, l) => s + l.left, 0);
}
const eligible = (l) => !l.promotionId && !l.regulatedPrice && dec(l.discountPct ?? 0).isZero() && l.originLine === undefined;
/**
 * Usa pontos numa venda: desconto até ao menor de (pontos disponíveis, 50% das
 * linhas elegíveis), como percentagem igual nas linhas elegíveis (2 casas, por
 * defeito). Os pontos gastos são o desconto que resultou, arredondado ao metical.
 */
export function applyLoyalty(lines, available) {
    const base = lines.filter(eligible);
    if (!base.length || available <= 0)
        return { lines, pointsUsed: 0, discount: '0.00' };
    const baseTotal = new D(calcDocument({ lines: base }).grossTotal);
    const cap = D.min(new D(available), baseTotal.times(MAX_REDEEM_PCT).div(100)).floor();
    if (cap.lessThanOrEqualTo(0))
        return { lines, pointsUsed: 0, discount: '0.00' };
    const pct = cap.div(baseTotal).times(100).toDecimalPlaces(2, D.ROUND_DOWN);
    if (pct.isZero())
        return { lines, pointsUsed: 0, discount: '0.00' };
    const out = lines.map((l) => (eligible(l) ? { ...l, discountPct: pct.toString(), loyalty: true } : l));
    const discount = new D(calcDocument({ lines: lines }).grossTotal).minus(calcDocument({ lines: out }).grossTotal);
    return { lines: out, pointsUsed: Math.min(available, discount.toDecimalPlaces(0).toNumber()), discount: discount.toFixed(2) };
}
//# sourceMappingURL=loyalty.js.map